/**
 * BildirimZili — her rolde kullanılan ortak sistem-içi bildirim bileşeni.
 *
 * Zil ikonu + okunmamış sayısı rozeti. Tıklayınca açılır panel:
 *  - Liste (yeni → eski), okunmamışlar vurgulu
 *  - Bir bildirime tıkla → okundu işaretle + varsa link'e git
 *  - "Tümünü okundu yap"
 *
 * Kanal: sadece sistemIci (KARAR #22). Telefon titremez.
 * kullaniciId = useAuth().kullanici.id (BOSS uid / personel id / yönetici uid).
 */

import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Bell, X, CheckCheck } from 'lucide-react'
import {
  kullaniciBildirimleri, okunmamisSayisi, bildirimOkundu,
  tumBildirimleriOkunduYap,
} from '@/lib/services/bildirim-service'
import type { Bildirim, FsTimestamp } from '@/types'

interface Props {
  kullaniciId: string
  className?: string
}

function gecenSure(ts?: FsTimestamp): string {
  const ms = ts?.toMillis?.()
  if (!ms) return ''
  const fark = Date.now() - ms
  const dk = Math.floor(fark / 60000)
  if (dk < 1) return 'az önce'
  if (dk < 60) return `${dk} dk önce`
  const sa = Math.floor(dk / 60)
  if (sa < 24) return `${sa} sa önce`
  const g = Math.floor(sa / 24)
  if (g === 1) return 'dün'
  if (g < 7) return `${g} gün önce`
  return ts!.toDate().toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })
}

export function BildirimZili({ kullaniciId, className }: Props) {
  const navigate = useNavigate()
  const [acik, setAcik] = useState(false)
  const [liste, setListe] = useState<Bildirim[]>([])
  const [okunmamis, setOkunmamis] = useState(0)
  const [yukleniyor, setYukleniyor] = useState(false)
  const islemRef = useRef(false)

  const sayiYenile = useCallback(async () => {
    if (!kullaniciId) return
    try {
      setOkunmamis(await okunmamisSayisi(kullaniciId))
    } catch (e) {
      console.error('Bildirim sayısı alınamadı:', e)
    }
  }, [kullaniciId])

  // Açılışta + her 60 sn'de okunmamış sayısını tazele.
  useEffect(() => {
    sayiYenile()
    const t = setInterval(sayiYenile, 60000)
    return () => clearInterval(t)
  }, [sayiYenile])

  const listeYukle = useCallback(async () => {
    if (!kullaniciId) return
    try {
      setYukleniyor(true)
      const b = await kullaniciBildirimleri(kullaniciId, 50)
      setListe(b)
      setOkunmamis(b.filter((x) => !x.okundu).length)
    } catch (e) {
      console.error('Bildirimler alınamadı:', e)
    } finally {
      setYukleniyor(false)
    }
  }, [kullaniciId])

  function ac() {
    setAcik(true)
    listeYukle()
  }

  async function bildirimTikla(b: Bildirim) {
    if (islemRef.current) return
    islemRef.current = true
    try {
      if (!b.okundu) {
        await bildirimOkundu(b.id)
        setListe((eski) =>
          eski.map((x) => (x.id === b.id ? { ...x, okundu: true } : x))
        )
        setOkunmamis((n) => Math.max(0, n - 1))
      }
      if (b.link) {
        setAcik(false)
        navigate(b.link)
      }
    } catch (e) {
      console.error('Bildirim açılamadı:', e)
    } finally {
      islemRef.current = false
    }
  }

  async function tumunuOkunduYap() {
    try {
      await tumBildirimleriOkunduYap(kullaniciId)
      setListe((eski) => eski.map((x) => ({ ...x, okundu: true })))
      setOkunmamis(0)
    } catch (e) {
      console.error('Tümü okundu yapılamadı:', e)
    }
  }

  return (
    <>
      <button
        onClick={ac}
        className={`relative p-2 hover:bg-gray-100 rounded-lg text-gray-600 ${className ?? ''}`}
        title="Bildirimler"
        aria-label="Bildirimler"
      >
        <Bell className="w-5 h-5" />
        {okunmamis > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
            {okunmamis > 99 ? '99+' : okunmamis}
          </span>
        )}
      </button>

      {acik && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4"
          onClick={() => setAcik(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl w-full max-w-sm mt-16 max-h-[80vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-brand-600" />
                <h2 className="font-bold text-gray-900">Bildirimler</h2>
                {okunmamis > 0 && (
                  <span className="text-xs font-semibold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                    {okunmamis} yeni
                  </span>
                )}
              </div>
              <button
                onClick={() => setAcik(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
                aria-label="Kapat"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {liste.some((b) => !b.okundu) && (
              <button
                onClick={tumunuOkunduYap}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-50 border-b border-gray-100"
              >
                <CheckCheck className="w-4 h-4" /> Tümünü okundu yap
              </button>
            )}

            <div className="flex-1 overflow-y-auto">
              {yukleniyor ? (
                <div className="text-center py-10">
                  <div className="inline-block animate-spin rounded-full h-7 w-7 border-b-2 border-brand-600"></div>
                </div>
              ) : liste.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Bell className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Henüz bildirim yok</p>
                </div>
              ) : (
                <div className="divide-y divide-gray-100">
                  {liste.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => bildirimTikla(b)}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex gap-3 ${
                        b.okundu ? 'bg-white' : 'bg-brand-50/60'
                      }`}
                    >
                      <span
                        className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${
                          b.okundu ? 'bg-transparent' : 'bg-brand-600'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`text-sm ${b.okundu ? 'font-medium text-gray-800' : 'font-bold text-gray-900'}`}>
                            {b.baslik}
                          </p>
                          <span className="text-[10px] text-gray-400 flex-shrink-0 whitespace-nowrap">
                            {gecenSure(b.olusturmaTarihi)}
                          </span>
                        </div>
                        <p className="text-xs text-gray-600 mt-0.5 break-words">{b.mesaj}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
